/**
 * Runtime configuration, validated once at boot.
 *
 * Two layers, deliberately:
 * - build-time flags (`import.meta.env`) — only what must change the bundle:
 *   whether /test and the mock engine exist.
 * - runtime values (`window.__APP_CONFIG__`, from `/config.js`) — API URL,
 *   analytics keys. The nginx entrypoint renders that file from container env,
 *   so one image serves any environment and nothing secret is baked in.
 *   (deepdive-v2 baked these in as Docker ARGs that did not match CI.)
 */
import { z } from 'zod/mini';

const RuntimeConfig = z.object({
  apiBaseUrl: z._default(z.union([z.url(), z.literal('')]), ''),
  appEnv: z._default(z.enum(['local', 'staging', 'production']), 'local'),
  posthogKey: z.optional(z.string()),
  posthogHost: z.optional(z.url()),
});

export type RuntimeConfig = z.infer<typeof RuntimeConfig>;

function readConfig(): RuntimeConfig {
  const fromWindow = typeof window !== 'undefined' ? (window.__APP_CONFIG__ ?? {}) : {};
  const merged = {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    appEnv: import.meta.env.VITE_APP_ENV,
    ...Object.fromEntries(Object.entries(fromWindow).filter(([, v]) => v !== '' && v != null)),
  };
  const parsed = RuntimeConfig.safeParse(merged);
  if (!parsed.success) {
    // Fail loudly and early rather than 500 on the first request.
    throw new Error(`Invalid runtime config (/config.js): ${parsed.error.message}`);
  }
  return parsed.data;
}

export const env = {
  ...readConfig(),
  testRoutesEnabled: import.meta.env.VITE_ENABLE_TEST_ROUTES === 'true',
  isDev: import.meta.env.DEV,
  /**
   * Dev server only: mount every route under `/` regardless of `live`, so a
   * widget can be tested against a local API before its whole page is live.
   * Unimplemented widgets say "not connected". Always false in a build.
   */
  livePreview: import.meta.env.DEV && import.meta.env.VITE_LIVE_PREVIEW === 'true',
} as const;
